import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Alert,
  FormHelperText
} from '@mui/material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useUser } from '../../../contexts/UserContext';

interface NewChannelDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  onChannelCreated: () => void;
}

const NewChannelDialog: React.FC<NewChannelDialogProps> = ({ open, onClose, organizationId, onChannelCreated }) => {
  const { currentUser } = useUser();
  const [channelName, setChannelName] = useState('');
  const [channelDescription, setChannelDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!channelName.trim()) {
      setError('Channel name is required');
      return;
    }

    if (!currentUser) {
      setError('You must be logged in to create a channel');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Format the channel name (lowercase, replace spaces with hyphens)
      const formattedName = channelName.trim().toLowerCase().replace(/\s+/g, '-');

      // Create the channel document in Firestore
      await addDoc(collection(db, 'organizations', organizationId, 'channels'), {
        name: formattedName,
        displayName: channelName.trim(),
        description: channelDescription.trim(),
        type: isPrivate ? 'private' : 'public',
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        members: [currentUser.uid], // Add creator as first member
        memberCount: 1
      });

      // Log activity
      await addDoc(collection(db, 'organizations', organizationId, 'activityLogs'), {
        userId: currentUser.uid,
        action: 'channel_created',
        details: {
          channelName: channelName.trim(),
          channelType: isPrivate ? 'private' : 'public'
        },
        timestamp: serverTimestamp()
      });

      // Reset form and close dialog
      setChannelName('');
      setChannelDescription('');
      setIsPrivate(false);
      onChannelCreated();
      onClose();
    } catch (err) {
      console.error('Error creating channel:', err);
      setError('Failed to create channel. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Channel</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Channel Name"
            fullWidth
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
            required
            sx={{ mb: 2 }}
            helperText="Choose a short, descriptive name for your channel"
          />
          
          <TextField
            margin="dense"
            label="Channel Description"
            fullWidth
            value={channelDescription}
            onChange={(e) => setChannelDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            helperText="Briefly describe the channel's purpose"
          />
          
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  color="primary"
                />
              }
              label="Private Channel"
            />
            <FormHelperText>
              {isPrivate 
                ? "Only invited members can join this channel" 
                : "Anyone in the organization can join this channel"}
            </FormHelperText>
          </Box>

          <Typography variant="body2" color="text.secondary">
            By creating a channel, you'll automatically become the channel admin.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading || !channelName.trim()}
          >
            {loading ? 'Creating...' : 'Create Channel'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NewChannelDialog;
